import { useRef } from 'react';

import cs from 'classnames';
import Tippy from '@tippyjs/react';
import { useRecoilValue } from 'recoil';

import { cn, ia, pm, spaceToKebabCase } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import { permissions as permissionsState } from 'components/state';

import { generateSubmenuKey } from '../Panel/lib/subPanelPosition';
import { focusFirstMenuItem, handleSubmenuTriggerKeyDown } from '../../lib/menuKeyboard';

import OptionContent from './components/OptionContent';
import PopoverSubmenu from './components/PopoverSubmenu';
import { getIsOptionActive } from './lib/getIsOptionActive';
import { getSubmenuAriaProps, getSubmenuId } from './lib/getSubmenuAriaProps';

const PopoverSeparator = ({ className }) => (
  <div role="separator" className={cs('my-1 h-[1px] bg-neutral-200', className)} />
);

const PopoverOption = ({
  option,
  index,
  level,
  handleClose,
  parentPath = '',
  hoveredIndexes,
  handleMouseEnter,
  handleMouseLeave,
  handleSubmenuClose,
  handleOptionClick,
  activeOptionRef,
  submenuRefs,
  text,
  customButton,
  optionIconSize,
  optionIconColor,
  iconsShade,
  optionArrowIcon,
  optionArrowIconStroke,
  optionActiveIcon,
  optionArrowColor,
  position,
  renderOptions,
  separatorClassName,
  optionIconNoColor
}) => {
  const permissions = useRecoilValue(permissionsState);
  const anchorRef = useRef(null);
  const focusSubmenuOnOpenRef = useRef(false);

  if (option.type === 'separator' || option.separator) {
    return (
      <PopoverSeparator
        key={`separator-${index}`}
        className={cs(option.className, separatorClassName)}
      />
    );
  }

  if (option?.permission) {
    const hasPermission = option?.permission?.isAny
      ? pm(permissions, option?.permission?.requiredPermissions, 'or')
      : pm(permissions, option?.permission?.requiredPermissions);

    if (!hasPermission) return null;
  }

  const label = option.label || option.title;
  const isActive = getIsOptionActive({ option, label, text, customButton });

  const currentPath = generateSubmenuKey(parentPath, level, index);
  const submenuKey = currentPath;
  const isHovered = hoveredIndexes[level] === index;
  // Content-only rows (e.g. a section heading passed as `component`) aren't actions.
  const isMenuItem = !!(label || option.onClick || ia(option.children));

  const openSubmenu = () => {
    if (option.disabled || option.loading) return;
    if (isHovered) return focusFirstMenuItem(document.getElementById(getSubmenuId(submenuKey)));
    focusSubmenuOnOpenRef.current = true;
    handleMouseEnter({ level, option, index });
  };

  return (
    <Tippy
      content={option.tooltip}
      disabled={!option.tooltip}
      placement="left"
      className="tippy-dark">
      <div
        key={index}
        ref={(node) => {
          anchorRef.current = node;
          if (isActive && activeOptionRef) activeOptionRef.current = node;
        }}
        role={isMenuItem ? 'menuitem' : 'none'}
        aria-disabled={(isMenuItem && option.disabled) || undefined}
        tabIndex={isMenuItem ? (isActive ? 0 : -1) : undefined}
        className={cn(
          'relative flex shrink-0 items-center justify-between gap-x-2 outline-none',
          typeof option.component !== 'function' &&
            'cursor-pointer py-[6px] !pl-4 pr-2 hover:bg-neutral-50',
          isMenuItem && typeof option.component !== 'function' && 'focus:bg-neutral-50',
          option.className,
          isHovered && typeof option.component !== 'function' && 'bg-neutral-50',
          option.onClick && !ia(option.children) ? 'cursor-pointer pr-4' : 'cursor-default',
          isActive && 'cursor-default bg-neutral-75 !pr-2 hover:bg-neutral-75',
          option.loading && 'cursor-not-allowed'
        )}
        data-qa={`popover-option-${spaceToKebabCase(label)}`}
        {...getSubmenuAriaProps({ option, isHovered, submenuKey })}
        onMouseEnter={() => handleMouseEnter({ level, option, index })}
        onMouseLeave={(event) => handleMouseLeave({ level, option, event })}
        // Focus never opens a submenu (that's ArrowRight/Enter); on a leaf it closes an open sibling's.
        onFocus={(event) =>
          event.target === event.currentTarget &&
          !ia(option.children) &&
          handleMouseEnter({ level, option, index })
        }
        onKeyDown={(event) =>
          ia(option.children) && handleSubmenuTriggerKeyDown(event, openSubmenu)
        }
        onClick={(event) =>
          !option.disabled && !option.loading && handleOptionClick(option, handleClose, event)
        }>
        {/* Disabled overlay */}
        {option.disabled && (
          <div className="absolute inset-0 z-[100] cursor-not-allowed bg-white opacity-40"></div>
        )}

        <OptionContent
          option={option}
          label={label}
          isHovered={isHovered}
          optionIconSize={optionIconSize}
          optionIconColor={optionIconColor}
          optionIconNoColor={optionIconNoColor}
          iconsShade={iconsShade}
        />

        {/* Component or arrow/active icon */}
        {typeof option.component === 'object' ? (
          option.component
        ) : typeof option.component === 'function' ? (
          option.component({ handleClose })
        ) : ia(option.children) ? (
          <Icon
            icon={optionArrowIcon}
            size={optionIconSize}
            className="ml-auto"
            color={optionArrowColor || optionIconColor}
            shade={iconsShade}
            stroke={optionArrowIconStroke}
          />
        ) : (
          optionActiveIcon &&
          isActive && (
            <Icon
              icon={optionActiveIcon}
              size={19}
              className="ml-auto"
              color={optionIconColor}
              shade={iconsShade}
            />
          )
        )}

        {ia(option.children) && isHovered && (
          <PopoverSubmenu
            submenuKey={submenuKey}
            submenuRefs={submenuRefs}
            anchorRef={anchorRef}
            focusOnOpenRef={focusSubmenuOnOpenRef}
            position={position}
            optionPosition={option.position}
            onMouseEnter={() => handleMouseEnter({ level, option, index })}
            onMouseLeave={(event) => handleMouseLeave({ level, option, event })}
            onClose={() => handleSubmenuClose(level)}>
            {renderOptions(option.children, level + 1, handleClose, currentPath)}
          </PopoverSubmenu>
        )}
      </div>
    </Tippy>
  );
};

export default PopoverOption;
